import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import {
  updateProfileInfo,
  getUserInfo,
  updateProfilePicture,
} from "../../service/User";
import dummyImg from "../../assets/dummy.webp";
import { deleteS3Object, uploadFileToS3 } from "../../service/S3Bucket";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { HiOutlineArrowLongLeft } from "react-icons/hi2";
import CommonButton from "../../components/common/CommonButton";
import { resolveAvatarSrc } from "../../utils/avatar";

const ProfileSection = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { profilePic, setProfilePic, langMode } = useContext(AppContext);
  const [userInfo, setUserInfo] = useState(null);
  const [initialUserInfo, setInitialUserInfo] = useState(null);
  const { theme } = useContext(AppContext);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isPasswordCleared, setIsPasswordCleared] = useState(false);
  const [uploadedImg, setUploadedImg] = useState('')
  const [selectedFile, setSelectedFile] = useState('')
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  useEffect(() => {
    const cookieProfilePic = Cookies.get("profilePic");
    setProfilePic(
      resolveAvatarSrc({
        contextProfilePic: cookieProfilePic,
        dummyImg,
      })
    );
  }, []);
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  async function deleteExistingImage() {
    const oldPic = Cookies.get("profilePic");
    if (!oldPic || oldPic === "null") return; // No image to delete

    try {
      const urlParts = oldPic.split('/');
      const oldKey = urlParts.slice(3).join('/'); // Extracts key from S3 URL
      console.log("Deleting S3 object with key:", oldKey);

      await deleteS3Object({ Key: oldKey }); // Pass as object with Key property
    } catch (error) {
      console.error("Error deleting existing S3 image:", error);
    }
  }

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];

    if (!file || !allowedTypes.includes(file.type)) {
      toast.error(translations[langMode].invalidfiletype);
      return;
    }
    setUploadedImg(URL.createObjectURL(file));
    setSelectedFile(file); // Store file for later upload
  };

  const navigate = useNavigate();
  const ChangePassword = () => {
    if (userInfo?.email) {
      navigate("/reset-password", {
        state: { emaill: userInfo.email, fromProfile: true },
      });
    } else {
      toast.error(translations[langMode].emailnotavailble);
    }
  };
  useEffect(() => {
    GetUserInfo();
  }, []);

  const sanitizeUserInfo = (data) => {
    if (!data) return null;
    return { ...data, password: "" };
  };

  const GetUserInfo = async () => {
    setLoading(true);
    try {
      const data = await getUserInfo();
      const sanitized = sanitizeUserInfo(data);
      setUserInfo(sanitized);
      setInitialUserInfo(sanitized);
      setIsPasswordCleared(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    const value = password || "";
    return (
      value.length >= 8 &&
      /[A-Z]/.test(value) &&
      /[a-z]/.test(value) &&
      /\d/.test(value) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(value)
    );
  };

  const handleFieldChange = (e) => {
    if (!userInfo) return;

    const { name, value } = e.target;

    if (name === "password") {
      setIsPasswordCleared(value.length > 0);
    }

    setUserInfo((prevUserInfo) => ({
      ...prevUserInfo,
      [name]: value,
    }));
  };

  const handleCancle = () => {
    const path = Cookies.get('previousPath')
    navigate(path);
  };

  const handleSaveChanges = async () => {
    if (selectedFile) {
      const uploadedPhotoUrl = await uploadFileToS3(selectedFile);
      if (!uploadedPhotoUrl) {
        throw new Error("Upload failed or returned empty URL");
      }
      setProfilePic(uploadedPhotoUrl);
      await updateProfilePicture(uploadedPhotoUrl);
    }

    if (isPasswordCleared && !validatePassword(userInfo.password)) {
      toast.error(translations[langMode].passowrdmust);
      return
    }
    try {
      await updateProfileInfo(
        userInfo.userId,
        userInfo.name,
        userInfo.email,
        userInfo.phone,
        isPasswordCleared ? userInfo.password : "",
        isPasswordCleared,
      );
      sessionStorage.setItem("userDetails", JSON.stringify(userInfo));
      Cookies.set("userType", userInfo.userType, {
        secure: true,
        sameSite: "strict",
      });
      Cookies.set("userId", userInfo.userId);
      Cookies.set("userName", userInfo.name);
      Cookies.set("userVisibility", userInfo.userVisibility);
      Cookies.set("userEmail", userInfo.email, { httpOnly: true });
      GetUserInfo();
      toast.success(translations[langMode].profileupdated);
    } catch (error) { }
  };

  const handleGoBack = () => {
    const path = Cookies.get('previousPath')
    navigate(path);
  };
  const profileImageSrc = resolveAvatarSrc({
    uploadedImg,
    contextProfilePic: profilePic,
    dummyImg,
  });

  const themeModeClass =
    theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "";
  const cardClass = `rounded-2xl border border-gray-200 bg-white shadow-sm ${themeModeClass}`;
  const sectionLabelClass = "text-xs font-semibold uppercase tracking-wide text-gray-500";
  const inputBaseClass = `block w-full rounded-lg border border-[#D1D5DB] bg-transparent p-3 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-0 focus:border-gray-500 ${themeModeClass}`;

  const passwordValue = userInfo?.password || "";
  const passwordChecklist = [
    { label: "At least 8 characters", valid: passwordValue.length >= 8 },
    { label: "One uppercase letter", valid: /[A-Z]/.test(passwordValue) },
    { label: "One lowercase letter", valid: /[a-z]/.test(passwordValue) },
    { label: "One number", valid: /\d/.test(passwordValue) },
    {
      label: "One special character",
      valid: /[!@#$%^&*(),.?":{}|<>]/.test(passwordValue),
    },
  ];

  return (
    <div className="w-full">
      {loading && <Spinner />}

      <div className="mx-2 mb-3 flex items-center gap-3 sm:mx-4">
        <button
          onClick={handleGoBack}
          className="flex h-9 w-9 min-h-[36px] items-center justify-center rounded-full bg-blue-100 p-1 text-blue-800 shadow-md transition duration-200 ease-in-out hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75"
          aria-label="Go back"
        >
          <HiOutlineArrowLongLeft size={24} />
        </button>
        <p className="text-sm font-semibold uppercase tracking-wide sm:text-base">
          {translations[langMode].profile}
        </p>
      </div>

      <div className={`mx-2 rounded-2xl p-4 sm:mx-4 sm:p-6 ${themeModeClass}`}>
        <div className={`mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 sm:p-6 ${cardClass}`}>
          <div className="flex flex-col gap-4 rounded-xl border border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <img
                key={profilePic}
                className="h-20 w-20 rounded-full border-2 border-gray-300 bg-white p-1"
                src={profileImageSrc}
                alt="profileimg"
              />
              <div className="space-y-1">
                <p className={sectionLabelClass}>{translations[langMode].profile}</p>
                <button
                  type="button"
                  className="cursor-pointer text-sm font-semibold text-blue-700 underline"
                  onClick={triggerFileInput}
                >
                  {translations[langMode].changeprofile}
                </button>
              </div>
            </div>
            <input
              type="file"
              name="media"
              onChange={handleImageUpload}
              accept="image/jpeg, image/png, image/gif, image/jpg"
              className="uploadFile hidden"
              ref={fileInputRef}
            />
          </div>

          <div className="space-y-5">
            <h3 className={sectionLabelClass}>{translations[langMode].basicinfo}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm">{translations[langMode].username}</label>
                <input
                  type="text"
                  value={userInfo && userInfo.name}
                  onChange={handleFieldChange}
                  name="name"
                  className={inputBaseClass}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm">{translations[langMode].userType}</label>
                <input
                  type="text"
                  value={userInfo && userInfo.userType}
                  name="userType"
                  className={`${inputBaseClass} cursor-none bg-gray-50`}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm">{translations[langMode].Email}</label>
                <input
                  type="text"
                  value={userInfo && userInfo.email}
                  onChange={handleFieldChange}
                  name="email"
                  className={inputBaseClass}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm">{translations[langMode].Phone}</label>
                <input
                  type="text"
                  value={userInfo && userInfo.phone}
                  onChange={handleFieldChange}
                  name="phone"
                  className={inputBaseClass}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm">{translations[langMode].UserId}</label>
                <input
                  type="text"
                  value={userInfo && userInfo.userId}
                  name="userId"
                  className={inputBaseClass}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm">{translations[langMode].password}</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={userInfo?.password ?? ""}
                    onChange={handleFieldChange}
                    name="password"
                    autoComplete="new-password"
                    placeholder={translations[langMode]?.leaveBlankToKeepPassword || "Leave blank to keep current password"}
                    className={`${inputBaseClass} pr-10`}
                  />

                  <button
                    type="button"
                    disabled={!isPasswordCleared}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-600"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                {isPasswordCleared && (
                  <ul className="mt-1 grid grid-cols-1 gap-x-2 gap-y-1 sm:grid-cols-2">
                    {passwordChecklist.map((rule) => (
                      <li
                        key={rule.label}
                        className={`flex items-center gap-2 text-[11px] ${rule.valid ? "text-green-600" : "text-gray-400"
                          }`}
                      >
                        <span>{rule.valid ? "?" : "?"}</span>
                        <span>{rule.label}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="pt-2">
            <hr className="w-full border-gray-300" />
            <div className="flex flex-col-reverse items-stretch justify-end gap-3 pt-5 sm:flex-row sm:items-center">
              <CommonButton colorIntent="danger"
                type="button"
                onClick={handleCancle}
                variant="danger"
                tooltipText={translations[langMode].cancelanddiscardchanges}
                tooltipPosition="top"
              >
                {translations[langMode].cancel}
              </CommonButton>
              <CommonButton colorIntent="success"
                type="button"
                onClick={handleSaveChanges}
                variant="primary"
                tooltipText={translations[langMode].savechangesforprofile}
                tooltipPosition="top"
              >
                {translations[langMode].save}
              </CommonButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ProfileSection;
